import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';

function getSouthAfricanHour(): number {
  const now = new Date();
  const saTime = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Africa/Johannesburg',
    hour: 'numeric',
    hour12: false,
  }).format(now);
  return parseInt(saTime, 10);
}

interface TimeBasedHelperProps {
  morningCompleted: boolean;
  eveningCompleted: boolean;
}

export function TimeBasedHelper({ morningCompleted, eveningCompleted }: TimeBasedHelperProps) {
  const [currentHour, setCurrentHour] = useState(getSouthAfricanHour());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentHour(getSouthAfricanHour());
    }, 60000); // Update every minute
    return () => clearInterval(timer);
  }, []);

  // Determine what message to show
  const getMessage = () => {
    // Morning (6 AM - 12 PM)
    if (currentHour >= 6 && currentHour < 12 && !morningCompleted) {
      return {
        icon: '☀️',
        title: 'Good morning!',
        message: "Time for your morning stock count",
        action: 'Start Morning Count',
        variant: 'primary' as const,
      };
    }

    // Afternoon (12 PM - 5 PM)
    if (currentHour >= 12 && currentHour < 17 && morningCompleted && !eveningCompleted) {
      return {
        icon: '📦',
        title: 'Afternoon check',
        message: 'Have you recorded any restocks today?',
        action: 'Add Restock',
        variant: 'secondary' as const,
      };
    }

    // Evening (5 PM - 11 PM)
    if (currentHour >= 17 && currentHour < 23 && !eveningCompleted) {
      return {
        icon: '🌙',
        title: 'Evening time!',
        message: "Don't forget your evening stock count",
        action: 'Start Evening Count',
        variant: 'primary' as const,
      };
    }

    // Late (11 PM - 6 AM)
    if ((currentHour >= 23 || currentHour < 6) && morningCompleted && eveningCompleted) {
      return {
        icon: '✅',
        title: 'All done!',
        message: 'Great work today. See you tomorrow!',
        variant: 'success' as const,
      };
    }

    // Default - everything completed
    if (morningCompleted && eveningCompleted) {
      return {
        icon: '🎉',
        title: 'Looking good!',
        message: 'All counts are up to date',
        variant: 'success' as const,
      };
    }

    return null;
  };

  const helper = getMessage();

  if (!helper) return null;

  const variants = {
    primary: 'bg-gradient-gold border-primary text-dark',
    secondary: 'bg-dark-card border-accent/50 text-white',
    success: 'bg-dark-card border-success/50 text-white',
  };

  return (
    <div className={cn('rounded-lg p-4 border-2', variants[helper.variant])}>
      <div className="flex items-start gap-3">
        <div className="text-3xl">{helper.icon}</div>
        <div className="flex-1">
          <h3 className={cn('font-bold text-lg mb-1', helper.variant === 'primary' && 'text-dark')}>
            {helper.title}
          </h3>
          <p className={cn('text-sm', helper.variant === 'primary' ? 'text-dark/80' : 'text-gray-400')}>
            {helper.message}
          </p>
        </div>
      </div>
    </div>
  );
}
